import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  MapPinIcon,
  HandRaisedIcon,
  UserCircleIcon,
  ArrowRightIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import {
  mergeAgendaItems,
  getAgendaItemDate,
  getAgendaItemEndDate,
  getAgendaStatusBadge,
  agendaItemDateStrings,
} from '../utils/agendaHelpers';
import AgendaReunionLogo from '../components/AgendaReunionLogo';

// ── Helpers ───────────────────────────────────────────────────────────────────

const MONTHS_FR = [
  'Janvier','Février','Mars','Avril','Mai','Juin',
  'Juillet','Août','Septembre','Octobre','Novembre','Décembre',
];
const DAYS_FR = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];

const isoDate = (d) => d.toISOString().split('T')[0];

// ── Composant carte agenda (sidebar) ───────────────────────────────────────────

function AgendaCard({ item }) {
  const isReunion = item.kind === 'reunion';
  const linkTo = isReunion ? `/reunions/${item._id}` : `/events/${item._id}`;
  const badge = getAgendaStatusBadge(item);
  const date = isReunion
    ? { location: { name: item.lieu } }
    : item.dates?.[0];

  return (
    <Link
      to={linkTo}
      className="flex items-stretch gap-5 p-3 rounded-xl hover:bg-slate-50 transition-colors group"
    >
      {isReunion ? (
        <AgendaReunionLogo organisme={item.organisme} size="lg" />
      ) : item.coverImage?.url ? (
        <div className="flex-shrink-0 w-20 lg-w-30 h-20 rounded-lg overflow-hidden">
          <img src={item.coverImage.url} alt={item.title} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="flex-shrink-0 w-20 lg-w-30 h-20 rounded-lg bg-slate-100 flex items-center justify-center">
          <CalendarDaysIcon className="h-6 w-6 text-slate-300" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-md font-extrabold text-slate-800 line-clamp-1 group-hover:text-indigo-600 transition-colors">
          {item.title}
        </p>
        <div className="flex items-center gap-2 mt-1">
          {isReunion && item.organisme && (
            <p className="text-xs px-2.5 py-0.5 text-honey-600 mt-0.5 line-clamp-1 rounded-full font-medium bg-honey-100 text-honey-800">
              {item.organisme}
            </p>
          )}
          {isReunion && (
            <span className="inline-block text-xs px-2.5 py-0.5 rounded-full font-medium bg-purple-100 text-purple-800">
              Réunion convoquée
            </span>
          )}
        </div>
        { !isReunion && (
          <div className="mt-1 flex flex-wrap gap-1">
            <span className={`inline-block text-xs px-3 py-1 rounded-full font-medium ${badge.cls}`}>
              {badge.label}
            </span>
            { item.referents?.some((ref) => ref._id === item._currentUserId) && (
              <span className="inline-flex items-center text-xs px-2 py-1 rounded-full font-medium bg-purple-100 text-purple-800">
                <UserCircleIcon className="h-3 w-3 mr-1" />Référent
              </span>
            )}
            { item.volunteerStatus && (
              <span className="inline-flex items-center text-xs px-2 py-1 rounded-full font-medium bg-blue-100 text-blue-800">
                <HandRaisedIcon className="h-3 w-3 mr-1" />Volontaire
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 mt-2 text-xs text-slate-600">
          <div className="flex items-center gap-1">
            <CalendarIcon className="h-4 w-4 flex-shrink-0" />
            <span className="">{new Date(item.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
          </div>
          {date?.location?.name && (
            <p className="flex items-center gap-1">
              <MapPinIcon className="h-4 w-4 flex-shrink-0" />{date.location.name}
            </p>
          )} 
        </div>
      </div>
      <div className="flex-shrink-0 flex items-center self-center">
        <ArrowRightIcon className="h-5 w-5 text-purple-400 group-hover:text-purple-600 group-hover:translate-x-0.5 transition-all" />
      </div>
    </Link>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function EventsCalendar() {
  const { user } = useAuth();
  const [agendaItems, setAgendaItems] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selectedDay, setSelectedDay] = useState(isoDate(new Date()));

  const today = new Date();
  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const api = process.env.REACT_APP_API_URL;

    Promise.all([
      axios.get(`${api}/events/public?includePast=true`, { headers }),
      axios.get(`${api}/reunions/mine?includePast=true`, { headers }),
    ])
      .then(([eventsRes, reunionsRes]) => {
        setAgendaItems(mergeAgendaItems(eventsRes.data || [], reunionsRes.data || []));
      })
      .catch((err) => console.error('Erreur chargement agenda:', err))
      .finally(() => setLoading(false));
  }, [user]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
    setSelectedDay(null);
  };

  const dayEventMap = useMemo(() => {
    const map = {};
    agendaItems.forEach((item) => {
      const days = agendaItemDateStrings(item);
      days.forEach((d) => {
        if (!map[d]) map[d] = [];
        map[d].push(item);
      });
    });
    return map;
  }, [agendaItems]);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const totalCells  = Math.ceil((startOffset + daysInMonth) / 7) * 7;
    const cells = [];
    for (let i = 0; i < totalCells; i++) {
      const dayNum = i - startOffset + 1;
      if (dayNum < 1 || dayNum > daysInMonth) {
        cells.push(null);
      } else {
        const d = new Date(viewYear, viewMonth, dayNum);
        cells.push(isoDate(d));
      }
    }
    return cells;
  }, [viewYear, viewMonth]);

  const todayStr = isoDate(today);

  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    const up = [];
    const pa = [];
    agendaItems.forEach((item) => {
      const end = getAgendaItemEndDate(item);
      if (now > end) pa.push(item);
      else up.push(item);
    });
    up.sort((a, b) => getAgendaItemDate(a) - getAgendaItemDate(b));
    pa.sort((a, b) => getAgendaItemDate(b) - getAgendaItemDate(a));
    return { upcoming: up, past: pa };
  }, [agendaItems]);

  const selectedWithUser = useMemo(
    () => (selectedDay ? (dayEventMap[selectedDay] || []) : []).map((item) => ({ ...item, _currentUserId: user?._id })),
    [selectedDay, dayEventMap, user?._id]
  );

  const upcomingWithUser = useMemo(
    () => upcoming.map((item) => ({ ...item, _currentUserId: user?._id })),
    [upcoming, user?._id]
  );

  const pastWithUser = useMemo(
    () => past.map((item) => ({ ...item, _currentUserId: user?._id })),
    [past, user?._id]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 overflow-y-auto lg:overflow-hidden">

      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3">
        <CalendarDaysIcon className="h-5 w-5 text-indigo-600 flex-shrink-0" />
        <h1 className="text-lg font-bold text-gray-900">Agenda</h1>
      </div>

      <div className="flex-1 lg:overflow-hidden grid grid-cols-1 lg:grid-cols-3 gap-4 p-4">

        <div className="lg:col-span-2 flex flex-col lg:overflow-hidden gap-4">

          <div className="bg-white shadow rounded-xl flex flex-col overflow-hidden lg:flex-1">

            <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 border-b border-gray-100">
              <button
                onClick={prevMonth}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
                aria-label="Mois précédent"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <h2 className="text-base font-semibold text-gray-900 capitalize">
                {MONTHS_FR[viewMonth]} {viewYear}
              </h2>
              <button
                onClick={nextMonth}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
                aria-label="Mois suivant"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-shrink-0 grid grid-cols-7 border-b border-gray-100">
              {DAYS_FR.map((d) => (
                <div key={d} className="py-2 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {d}
                </div>
              ))}
            </div>

            <div
              className="lg:flex-1 grid grid-cols-7"
              style={{ gridAutoRows: '1fr' }}
            >
              {calendarDays.map((dayStr, i) => {
                if (!dayStr) {
                  return <div key={`empty-${i}`} className="min-h-[70px] bg-gray-50/50 border-b border-r border-gray-100" />;
                }
                const dayItems = dayEventMap[dayStr] || [];
                const isToday    = dayStr === todayStr;
                const isSelected = dayStr === selectedDay;
                const dayNum     = parseInt(dayStr.split('-')[2], 10);

                return (
                  <div
                    key={dayStr}
                    onClick={() => setSelectedDay(isSelected ? null : dayStr)}
                    className={`min-h-[70px] p-2 border-b border-r border-gray-100 cursor-pointer transition-colors overflow-hidden ${
                      isSelected ? 'bg-indigo-50' :
                      dayItems.length ? 'hover:bg-indigo-50/40' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium transition-colors ${
                      isToday    ? 'bg-indigo-600 text-white' :
                      isSelected ? 'bg-indigo-100 text-indigo-700' :
                      'text-gray-700'
                    }`}>
                      {dayNum}
                    </span>

                    <div className="mt-1 space-y-0.5">
                      {dayItems.slice(0, 2).map((item) => {
                        const isReunion = item.kind === 'reunion';
                        return (
                          <div
                            key={`${item.kind}-${item._id}`}
                            className="text-xs rounded px-1 truncate leading-5"
                            style={
                              isReunion
                                ? { backgroundColor: '#fef3c7', color: '#b45309' }
                                : { backgroundColor: '#eef2ff', color: '#4338ca' }
                            }
                            title={
                              isReunion && item.organisme
                                ? `${item.title} (${item.organisme})`
                                : item.title
                            }
                          >
                            {item.title} {isReunion && item.organisme && `(${item.organisme})`}
                          </div>
                        );
                      })}
                      {dayItems.length > 2 && (
                        <div className="text-xs text-gray-400 pl-1">+{dayItems.length - 2}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        <div className="lg:overflow-y-auto space-y-4 pb-4">

          {selectedDay && (
            <div className="bg-white shadow rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-indigo-100 flex items-center gap-2 bg-indigo-50">
                <span className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" />
                <h3 className="text-sm font-semibold text-indigo-800 capitalize">
                  {new Date(`${selectedDay}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </h3>
              </div>
              {selectedWithUser.length > 0 ? (
                <div className="divide-y divide-gray-50">
                  {selectedWithUser.map((item) => (
                    <AgendaCard key={`${item.kind}-${item._id}`} item={item} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-6">Rien de prévu ce jour.</p>
              )}
            </div>
          )}

          <div className="bg-white shadow rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" />
              <h3 className="text-sm font-semibold text-gray-800">À venir</h3>
              <span className="ml-auto text-xs text-gray-400 font-medium">{upcoming.length}</span>
            </div>
            <div className="divide-y divide-gray-50">
              {upcomingWithUser.length > 0 ? (
                upcomingWithUser.map((item) => (
                  <AgendaCard key={`${item.kind}-${item._id}`} item={item} />
                ))
              ) : (
                <p className="text-sm text-gray-400 text-center py-6">Rien de prévu.</p>
              )}
            </div>
          </div>

          {pastWithUser.length > 0 && (
            <div className="bg-white shadow rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0" />
                <h3 className="text-sm font-semibold text-gray-800">Passés</h3>
                <span className="ml-auto text-xs text-gray-400 font-medium">{past.length}</span>
              </div>
              <div className="divide-y divide-gray-50">
                {pastWithUser.map((item) => (
                  <AgendaCard key={`${item.kind}-${item._id}`} item={item} />
                ))}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
